/**
 * useSalesByItemData — Data-fetching hook for Sales by Item.
 *
 * Loads NetSuite sales-by-item data for a given portco and period.
 * Follows the same pattern as useInventoryData.ts.
 */

import { useEffect, useState } from "react";
import {
  getSalesByItem,
  SalesByItemRow,
  SalesPeriod,
} from "../utils/PowerBIApiCall";

export interface ProcessedSalesItem {
  item_name: string;
  display_name: string;
  item_type: string;
  product_category: string;
  product_category_2: string;
  revenue: number; // Negated (positive = revenue)
  cost: number; // COGS (positive)
  grossProfit: number; // revenue - cost
  grossMargin: number; // (grossProfit / revenue) * 100
  quantity: number;
  revenueShare: number; // Percentage of total
  rank: number;
  itemCount?: number; // Set on category-grouped rows only
}

export interface SalesByItemSummary {
  totalRevenue: number;
  totalCost: number;
  totalGrossProfit: number;
  totalGrossMargin: number;
  totalItems: number;
  totalQuantity: number;
  topItem: ProcessedSalesItem | null;
}

export interface SalesByItemData {
  items: ProcessedSalesItem[];
  summary: SalesByItemSummary;
  period: SalesPeriod;
  loading: boolean;
  error: string | null;
}

function processRows(rows: SalesByItemRow[]): {
  items: ProcessedSalesItem[];
  summary: SalesByItemSummary;
} {
  // Negate amounts (NS credit-normal: income is negative)
  // COGS is debit-normal (already positive)
  // Filter out NonInvtPart and items with zero revenue
  const mapped = rows
    .map((row) => {
      const revenue = -row.total_sales; // Negate to make revenue positive
      const cost = row.total_cost; // COGS already positive (debit-normal)
      const grossProfit = revenue - cost;
      const grossMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
      return {
        item_name: row.item_name,
        display_name: row.display_name || row.item_name,
        item_type: row.item_type,
        product_category: row.product_category || "",
        product_category_2: row.product_category_2 || "",
        revenue,
        cost,
        grossProfit,
        grossMargin,
        quantity: Math.abs(row.total_quantity),
      };
    })
    .filter((item) => item.item_type !== "NonInvtPart" && item.revenue > 0);

  // Sort by revenue descending
  mapped.sort((a, b) => b.revenue - a.revenue);

  const totalRevenue = mapped.reduce((sum, item) => sum + item.revenue, 0);
  const totalCost = mapped.reduce((sum, item) => sum + item.cost, 0);
  const totalQuantity = mapped.reduce((sum, item) => sum + item.quantity, 0);
  const totalGrossProfit = totalRevenue - totalCost;
  const totalGrossMargin =
    totalRevenue > 0 ? (totalGrossProfit / totalRevenue) * 100 : 0;

  // Assign rank and revenue share
  const items: ProcessedSalesItem[] = mapped.map((item, index) => ({
    ...item,
    rank: index + 1,
    revenueShare: totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0,
  }));

  return {
    items,
    summary: {
      totalRevenue,
      totalCost,
      totalGrossProfit,
      totalGrossMargin,
      totalItems: items.length,
      totalQuantity,
      topItem: items[0] || null,
    },
  };
}

export type SalesGroupBy = "item" | "category" | "category2";

/**
 * Aggregate processed item rows into product-category rows.
 * Returns items unchanged for "item"; otherwise one row per category
 * (blank categories bucketed as "Uncategorized"), revenue-ranked.
 */
export function groupItemsByCategory(
  items: ProcessedSalesItem[],
  groupBy: SalesGroupBy,
): ProcessedSalesItem[] {
  if (groupBy === "item") return items;
  const field =
    groupBy === "category" ? "product_category" : "product_category_2";

  const groups = new Map<string, ProcessedSalesItem>();
  for (const item of items) {
    const name = item[field] || "Uncategorized";
    let group = groups.get(name);
    if (!group) {
      group = {
        item_name: name,
        display_name: name,
        item_type: "Category",
        product_category: "",
        product_category_2: "",
        revenue: 0,
        cost: 0,
        grossProfit: 0,
        grossMargin: 0,
        quantity: 0,
        revenueShare: 0,
        rank: 0,
        itemCount: 0,
      };
      groups.set(name, group);
    }
    group.revenue += item.revenue;
    group.cost += item.cost;
    group.quantity += item.quantity;
    group.itemCount! += 1;
  }

  const grouped = Array.from(groups.values()).sort(
    (a, b) => b.revenue - a.revenue,
  );
  const totalRevenue = grouped.reduce((sum, g) => sum + g.revenue, 0);

  return grouped.map((group, index) => ({
    ...group,
    grossProfit: group.revenue - group.cost,
    grossMargin:
      group.revenue > 0
        ? ((group.revenue - group.cost) / group.revenue) * 100
        : 0,
    rank: index + 1,
    revenueShare: totalRevenue > 0 ? (group.revenue / totalRevenue) * 100 : 0,
  }));
}

/**
 * Fetch sales-by-item data for a portco.
 *
 * @param portcoId — slug from URL params
 * @param period — mtd | qtd | ytd
 * @param enabled — gate fetching (e.g. wait for MSAL interaction). Defaults to true.
 * @param onAuthError — optional callback for InteractionRequiredAuthError in live mode
 */
export function useSalesByItemData(
  portcoId: string | undefined,
  period: SalesPeriod = "ytd",
  enabled: boolean = true,
  onAuthError?: (error: unknown) => void,
): SalesByItemData {
  const [items, setItems] = useState<ProcessedSalesItem[]>([]);
  const [summary, setSummary] = useState<SalesByItemSummary>({
    totalRevenue: 0,
    totalCost: 0,
    totalGrossProfit: 0,
    totalGrossMargin: 0,
    totalItems: 0,
    totalQuantity: 0,
    topItem: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!portcoId || !enabled) return;

    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        const rows = await getSalesByItem(portcoId!, period);

        if (cancelled) return;

        const processed = processRows(rows);
        setItems(processed.items);
        setSummary(processed.summary);
      } catch (e) {
        if (cancelled) return;

        if (onAuthError) {
          const { InteractionRequiredAuthError } =
            await import("@azure/msal-browser");
          if (e instanceof InteractionRequiredAuthError) {
            onAuthError(e);
            return;
          }
        }

        setError(e instanceof Error ? e.message : "Failed to load sales data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [portcoId, period, enabled]);

  return { items, summary, period, loading, error };
}
